import { useEffect, useState } from 'react';
import api, { formatError } from '@/lib/api';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Loader2, FileText, Download, Mail } from 'lucide-react';

const DOCS = [['pan_card_doc', 'PAN Card'], ['aadhaar_doc', 'Aadhaar'], ['photo_doc', 'Photo'], ['signature_doc', 'Signature'], ['selfie_doc', 'Selfie']];
const FIELDS = [['full_name', 'Full Name'], ['date_of_birth', 'Date of Birth'], ['pan_number', 'PAN'], ['aadhaar_number', 'Aadhaar'],
  ['phone', 'Phone'], ['email', 'Email'], ['address', 'Address'], ['city', 'City'], ['state', 'State'], ['pin_code', 'PIN'],
  ['nominee_name', 'Nominee'], ['nominee_relationship', 'Nominee Relation'], ['occupation', 'Occupation'], ['income_source', 'Income Source']];

export const kycBadge = (s) => {
  const map = { verified: 'bg-emerald-100 text-emerald-700', rejected: 'bg-rose-100 text-rose-700' };
  return <Badge className={`border-0 text-[11px] capitalize ${map[s] || 'bg-amber-100 text-amber-700'}`}>{s || 'pending'}</Badge>;
};

const money = (v) => `$${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

function DocTile({ clientId, field, label }) {
  const [src, setSrc] = useState('');
  const [loading, setLoading] = useState(false);
  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get(`/sales/me/clients/${clientId}/kyc-doc/${field}`); setSrc(data.data_url); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); } finally { setLoading(false); }
  };
  const isPdf = src.startsWith('data:application/pdf');
  const tid = `kyc-doc-${label.toLowerCase().replace(/\s/g, '-')}`;
  if (!src) return (
    <button onClick={load} className="border border-brand-border rounded-lg h-[140px] flex flex-col items-center justify-center gap-2 hover:bg-brand-bg transition-colors" data-testid={tid}>
      {loading ? <Loader2 size={22} className="animate-spin text-brand-primary" /> : <FileText size={22} className="text-brand-primary" />}
      <span className="text-xs font-medium">{label}</span><span className="text-[10px] text-brand-text-secondary">Tap to view</span>
    </button>
  );
  return (
    <a href={src} target="_blank" rel="noreferrer" download={`${label}.${isPdf ? 'pdf' : 'png'}`}
       className="block border border-brand-border rounded-lg overflow-hidden hover:shadow-md transition-shadow" data-testid={`${tid}-open`}>
      {isPdf ? <div className="h-28 flex items-center justify-center bg-brand-bg"><FileText size={28} className="text-brand-primary" /></div>
        : <img src={src} alt={label} className="h-28 w-full object-cover" />}
      <p className="text-xs font-medium px-2 py-1.5 text-brand-text-primary">{label} · Download</p>
    </a>
  );
}

export function SalesClientDetail({ clientId, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!clientId) return;
    setData(null);
    api.get(`/sales/me/clients/${clientId}`).then(r => setData(r.data))
      .catch(err => { toast.error(formatError(err.response?.data?.detail)); onClose(); });
  }, [clientId, onClose]);

  const c = data?.client; const kyc = data?.kyc || {};
  const [sending, setSending] = useState(false);
  const emailProfile = async () => {
    setSending(true);
    try { const r = await api.post(`/sales/me/clients/${clientId}/email-profile`); toast.success(r.data.message); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); } finally { setSending(false); }
  };
  const downloadPdf = async () => {
    try {
      const r = await api.get(`/sales/me/clients/${clientId}/profile-pdf`, { responseType: 'blob' });
      const a = document.createElement('a'); a.href = URL.createObjectURL(r.data); a.download = `profile-${c.client_code}.pdf`; a.click();
    } catch (err) { toast.error('Could not download PDF'); }
  };
  return (
    <Dialog open={!!clientId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="sales-client-detail">
        {!data ? <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-brand-primary" /></div> : (
          <>
            <DialogHeader>
              <DialogTitle className="font-heading flex items-center gap-3">{c.name}
                <span className="font-mono text-xs text-brand-primary">{c.client_code}</span>{kycBadge(kyc.status === 'not_submitted' ? 'not submitted' : kyc.status)}
              </DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {[['Deposits', c.deposits], ['Withdrawals', c.withdrawals], ['Profit', c.profit], ['Main Balance', c.main_balance]].map(([l, v]) => (
                <div key={l} className="rounded-lg bg-brand-bg/60 p-3"><p className="text-[10px] uppercase tracking-widest text-brand-text-secondary font-semibold">{l}</p>
                  <p className="font-heading font-bold text-brand-text-primary" data-testid={`detail-${l.toLowerCase().replace(/\s/g, '-')}`}>{money(v)}</p></div>
              ))}
            </div>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" onClick={downloadPdf} className="h-8 text-xs" data-testid="client-profile-pdf-btn"><Download size={13} className="mr-1" /> Download Profile PDF</Button>
              <Button size="sm" onClick={emailProfile} disabled={sending || !c.email} className="h-8 text-xs bg-brand-primary text-white" data-testid="client-profile-email-btn">
                {sending ? <Loader2 size={13} className="animate-spin mr-1" /> : <Mail size={13} className="mr-1" />} Email Profile to Client
              </Button>
            </div>
            <section>
              <h4 className="text-xs uppercase tracking-widest font-semibold text-brand-text-secondary mb-2">KYC Details</h4>
              {kyc.status === 'not_submitted' ? <p className="text-sm text-brand-text-secondary">Client has not submitted KYC yet.</p> : (
                <>
                  <dl className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-2 text-sm">
                    {FIELDS.filter(([k]) => kyc[k]).map(([k, l]) => (
                      <div key={k}><dt className="text-[11px] text-brand-text-secondary">{l}</dt><dd className="font-medium text-brand-text-primary break-words">{kyc[k]}</dd></div>
                    ))}
                  </dl>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
                    {DOCS.filter(([k]) => (kyc.documents || []).includes(k)).map(([k, l]) => <DocTile key={k} clientId={clientId} field={k} label={l} />)}
                  </div>
                </>
              )}
            </section>
            <section className="grid sm:grid-cols-2 gap-4">
              <div>
                <h4 className="text-xs uppercase tracking-widest font-semibold text-brand-text-secondary mb-2">Transactions</h4>
                <div className="space-y-1 max-h-56 overflow-y-auto">
                  {data.transactions.length === 0 ? <p className="text-sm text-brand-text-secondary">None</p> : data.transactions.map(t => (
                    <div key={t.id} className="flex justify-between text-xs border-b border-brand-border/50 py-1.5">
                      <span className="capitalize">{t.type.replace('_', ' ')} <span className="text-brand-text-secondary">· {t.status}</span></span>
                      <span className="font-medium">{money(t.amount)} <span className="text-brand-text-secondary ml-1">{t.created_at.slice(0, 10)}</span></span>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <h4 className="text-xs uppercase tracking-widest font-semibold text-brand-text-secondary mb-2">Profit / Loss Records</h4>
                <div className="space-y-1 max-h-56 overflow-y-auto">
                  {data.pnl.length === 0 ? <p className="text-sm text-brand-text-secondary">None</p> : data.pnl.map(p => (
                    <div key={p.id} className="flex justify-between text-xs border-b border-brand-border/50 py-1.5">
                      <span>{p.date} <span className="text-brand-text-secondary">{p.symbol}</span></span>
                      <span className={`font-medium ${p.net_pnl >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>{money(p.net_pnl)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
